import type { Point } from "@/core/types";
import { cellKey } from "@/levels/geometry";

export interface WallHazardContext {
  cols: number;
  rows: number;
  obstacleKeys: ReadonlySet<string>;
  /** Whether WORMHOLE is active for this move - while active, crossing an outer edge wraps instead of killing, so an out-of-bounds point is never itself a lethal wall. */
  wormholeActive: boolean;
}

function isOutOfBounds(p: Point, cols: number, rows: number): boolean {
  return p.x < 0 || p.x >= cols || p.y < 0 || p.y >= rows;
}

function wrapPoint(p: Point, cols: number, rows: number): Point {
  return { x: ((p.x % cols) + cols) % cols, y: ((p.y % rows) + rows) % rows };
}

/**
 * Wall/obstacle lethality only - deliberately never body - for LAST-SECOND
 * TURN's "would going straight hit a lethal outer wall or fixed obstacle"
 * check (self/body danger alone never qualifies for that maneuver). Under
 * WORMHOLE, an out-of-bounds point wraps to the opposite edge instead of
 * being a lethal wall - but the wrapped-to cell can still be lethal if
 * it's a fixed obstacle, mirroring `LevelGame.wrapIfOutOfBounds()` +
 * `isObstacle()`'s own real movement rule exactly.
 */
export function isWallOrObstacleLethal(p: Point, ctx: WallHazardContext): boolean {
  if (isOutOfBounds(p, ctx.cols, ctx.rows)) {
    if (!ctx.wormholeActive) return true;
    return ctx.obstacleKeys.has(cellKey(wrapPoint(p, ctx.cols, ctx.rows)));
  }
  return ctx.obstacleKeys.has(cellKey(p));
}

export interface AnyHazardContext extends WallHazardContext {
  /** Live body segment cells, already reflecting this move's tail-vacating semantics (the post-movement body, not the pre-movement one). */
  bodyKeys: ReadonlySet<string>;
}

/**
 * Wall/obstacle/body lethality - used for THREAD THE NEEDLE's
 * squeezed-corridor perpendicular-cell check, where any of the three
 * hazard kinds (in any mix) can form one wall of the corridor. Same
 * WORMHOLE wall exclusion as `isWallOrObstacleLethal`, but the wrapped-to
 * cell is also checked against the body, not just obstacles.
 */
export function isAnyHazardLethal(p: Point, ctx: AnyHazardContext): boolean {
  if (isOutOfBounds(p, ctx.cols, ctx.rows)) {
    if (!ctx.wormholeActive) return true;
    const wrapped = wrapPoint(p, ctx.cols, ctx.rows);
    const key = cellKey(wrapped);
    return ctx.obstacleKeys.has(key) || ctx.bodyKeys.has(key);
  }
  const key = cellKey(p);
  return ctx.obstacleKeys.has(key) || ctx.bodyKeys.has(key);
}
